/**
 * The gate in front of the application.
 *
 * It renders the sign-in screen rather than redirecting to `/login`. A redirect would put
 * the unauthenticated state in the URL, which then has to be undone on success and kept in
 * step with wherever the user was trying to go. Swapping the subtree keeps the route intact,
 * so signing in leaves the person exactly where they were.
 *
 * This is a *usability* boundary, not the security boundary. Every endpoint enforces
 * ownership on the server; nothing here is trusted to keep anyone out.
 */
import type { ReactNode } from 'react'

import { SignInPage } from '@/routes/auth/SignInPage'
import { useAuth } from '@/features/auth/useAuth'

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    // Deliberately not a spinner: the session probe is usually a few milliseconds, and a
    // spinner that flashes for one frame reads as a glitch.
    return <div className="h-full bg-ground" aria-busy="true" aria-label="Checking session" />
  }

  if (!isAuthenticated) return <SignInPage />

  return <>{children}</>
}
